import { useEffect, useState, useCallback } from "react";
import {
  getClientDocuments,
  uploadClientDocument,
  getDocumentHistory,
  downloadClientDocument
} from "../api/clientDocumentApi";

import DocumentPreviewModal from "./DocumentPreviewModal";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg"];

const ClientDocuments = ({ clientId }) => {

  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [selectedFile, setSelectedFile] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);

  const loadDocs = useCallback(async () => {
    try {
      const res = await getClientDocuments(clientId);
      setDocs(res.data);
    } catch {
      setError("Failed to load documents");
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) loadDocs();
  }, [clientId, loadDocs]);

  // ================= Upload =================

  const handleUpload = async (docTypeId, file) => {

    setError("");

    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError("File must be less than 10 MB");
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Only PDF, JPG, PNG allowed");
      return;
    }

    try {

      setUploading(docTypeId);

      const formData = new FormData();
      formData.append("file", file);

      await uploadClientDocument(clientId, docTypeId, formData);

      await loadDocs();

    } catch {
      setError("Upload failed");
    } finally {
      setUploading(null);
    }
  };

  // ================= History =================

  const openHistory = async (doc) => {

    const res = await getDocumentHistory(
      clientId,
      doc.DocumentTypeID
    );

    setHistory(res.data);
    setHistoryDoc(doc);
  };

  const closeHistory = () => {
    setHistory([]);
    setHistoryDoc(null);
  };

  // ================= Preview =================

  const openPreview = (docId, fileName) => {
    setPreviewDoc({ docId, fileName });
  };

  const closePreview = () => {
    setPreviewDoc(null);
  };

  // ================= Download =================

  const downloadFile = (id) => {
    downloadClientDocument(id);
  };

  // ================= Status Badge =================

  const statusBadge = (doc) => {

    if (doc.FileName) {
      return (
        <span className="text-green-600 font-semibold">
          Uploaded
        </span>
      );
    }

    if (doc.IsMandatory) {
      return (
        <span className="text-red-600 font-semibold">
          Missing
        </span>
      );
    }

    return (
      <span className="text-slate-500">
        Optional
      </span>
    );
  };

  return (
    <div className="space-y-4">

      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}

      <table className="w-full border rounded">

        <thead className="bg-slate-100 text-left">
          <tr>
            <th className="p-3">Document</th>
            <th>Mandatory</th>
            <th>Status</th>
            <th>Version</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>

          {docs.map(d => (

            <tr key={d.DocumentTypeID} className="border-t">

              <td className="p-3 font-medium">
                {d.DocumentName}
              </td>

              <td>
                {d.IsMandatory ? "Yes" : "No"}
              </td>

              <td>
                {statusBadge(d)}
              </td>

              <td>
                {d.VersionNo ? `v${d.VersionNo}` : "-"}
              </td>

              <td className="space-x-2">

                {/* Upload */}
                <input
                  type="file"
                  className="text-sm"
                  onChange={(e) =>
                    setSelectedFile({
                      ...selectedFile,
                      [d.DocumentTypeID]: e.target.files[0]
                    })
                  }
                />

                <button
                  className="btn btn-sm btn-primary ms-2"
                  disabled={!selectedFile[d.DocumentTypeID] || uploading === d.DocumentTypeID}
                  onClick={() =>
                    handleUpload(
                      d.DocumentTypeID,
                      selectedFile[d.DocumentTypeID]
                    )
                  }
                >
                  {uploading === d.DocumentTypeID ? "Uploading..." : "Upload"}
                </button>

                {/* Download */}
                {d.ClientDocumentID && (
                  <button
                    className="text-blue-600 text-sm underline"
                    onClick={() =>
                      downloadFile(d.ClientDocumentID)
                    }
                  >
                    Download
                  </button>
                )}

                {/* Preview */}
                {d.ClientDocumentID && (
                  <button
                    className="text-teal-600 text-sm underline"
                    onClick={() =>
                      openPreview(
                        d.ClientDocumentID,
                        d.FileName
                      )
                    }
                  >
                    Preview
                  </button>
                )}

                {/* History */}
                {d.ClientDocumentID && (
                  <button
                    className="text-indigo-600 text-sm underline"
                    onClick={() => openHistory(d)}
                  >
                    History
                  </button>
                )}

              </td>

            </tr>

          ))}

        </tbody>
      </table>

      {/* ================= History Modal ================= */}

      {historyDoc && (

        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">

          <div className="bg-white p-6 rounded shadow w-[600px]">

            <div className="flex justify-between mb-4">
              <h3 className="font-semibold">
                {historyDoc.DocumentName} History
              </h3>

              <button
                onClick={closeHistory}
                className="text-red-500"
              >
                ✕
              </button>
            </div>

            <table className="w-full border">

              <thead className="bg-slate-100">
                <tr>
                  <th>Version</th>
                  <th>Date</th>
                  <th>Download</th>
                </tr>
              </thead>

              <tbody>

                {history.map(h => (
                  <tr key={h.ClientDocumentID} className="border-t">

                    <td className="p-2">
                      v{h.VersionNo}
                    </td>

                    <td>
                      {new Date(h.UploadedAt).toLocaleString()}
                    </td>

                    <td>
                      <button
                        className="text-blue-600 underline"
                        onClick={() =>
                          downloadFile(h.ClientDocumentID)
                        }
                      >
                        Download
                      </button>
                    </td>

                  </tr>
                ))}

              </tbody>

            </table>

          </div>
        </div>
      )}

      {/* ================= Preview Modal ================= */}

      {previewDoc && (
        <DocumentPreviewModal
          show={true}
          docId={previewDoc.docId}
          fileName={previewDoc.fileName}
          onClose={closePreview}
        />
      )}

    </div>
  );
};

export default ClientDocuments;
